import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import { motion, AnimatePresence } from "framer-motion";
import Hero from "../components/Hero";
import SEO from "../components/SEO";
import API from "../api";
import dayjs from "dayjs";
import { createArticleSchema } from "../utils/jsonLdSchemas";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const ArticlesPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("news");

  useEffect(() => {
    AOS.init({ duration: 700, once: true });
  }, []);

  // Ensure the page scrolls to top whenever this page is navigated to or the id changes
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window?.scrollTo) {
        // instant jump to top to avoid flashing at previous scroll position
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    } catch (e) {
      // fallback: simple assignment
      try {
        window.scrollTo(0, 0);
      } catch (err) {
        // ignore in environments without window
      }
    }
  }, []);

  /** Fetch all articles */
  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.get("/articles");
      const data =
        res.data && Array.isArray(res.data)
          ? res.data
          : res.data.articles || res.data.data || [];
      setArticles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch articles", err);
      setError(err.message || "Failed to load articles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  /** Filter articles by tab */
  const filteredArticles = articles.filter((article) => {
    const categoryLower = (article.category_name || "").toLowerCase();
    if (activeTab === "events") {
      return categoryLower.includes("event");
    } else {
      // Everything that isn't an event goes to news
      return !categoryLower.includes("event");
    }
  });

  const heroSlides = [
    {
      id: "articles-hero",
      image: "/assets/img/promo-1.png",
      title: "News & Events",
      subtitle: "Read the latest news, research updates and announcements from Africa Research University.",
      buttonText: "Browse Articles",
      buttonLink: "#articles",
    },
  ];

  return (
    <section className="bg-gray-50 min-h-screen pt-1">
      {/* Create JSON-LD for articles list */}
      {articles.length > 0 && (
        <SEO 
          title="News & Events — Africa Research University" 
          description="Latest news, research updates and events from Africa Research University."
          url="/articles"
          jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            'name': 'Articles',
            'url': 'https://africaresearchuniversity.com/articles',
            'description': 'Latest news and events from ARU',
            'hasPart': articles.slice(0, 10).map(article => createArticleSchema({
              title: article.title,
              excerpt: article.excerpt,
              description: article.content?.slice(0, 160),
              image: `${process.env.REACT_APP_API_BASE_URL}${article.image}`,
              datePublished: article.date,
              author: article.author || 'Africa Research University',
              url: `https://africaresearchuniversity.com/articles/${article.id}`
            }))
          }}
          breadcrumbs={[
            { name: 'Home', url: 'https://africaresearchuniversity.com' },
            { name: 'News & Events', url: 'https://africaresearchuniversity.com/articles' }
          ]}
        />
      )}
      {!articles.length && (
        <SEO 
          title="News & Events — Africa Research University" 
          description="Latest news, research updates and events from Africa Research University."
          url="/articles"
          breadcrumbs={[
            { name: 'Home', url: 'https://africaresearchuniversity.com' },
            { name: 'News & Events', url: 'https://africaresearchuniversity.com/articles' }
          ]}
        />
      )}
      {/* <Hero slides={heroSlides} interval={6000} height="300px" borderRadius="50px" hidden={true} /> */}

      <div className="container my-16 mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Latest Stories and Upcoming Events</h2>
          <p className="text-gray-600 mb-6">News, updates and Events from ARU.</p>

          {/* Tabs */}
          <div className="flex justify-center gap-3 mb-10">
            <button
              onClick={() => setActiveTab("news")}
              className={`px-5 py-2 rounded-full font-medium transition-all border ${activeTab === "news"
                ? "bg-green-600 text-white border-green-600 shadow-md"
                : "bg-white text-gray-700 border-gray-300 hover:bg-green-50"
                }`}
            >
              News
            </button>
            <button
              onClick={() => setActiveTab("events")}
              className={`px-5 py-2 rounded-full font-medium transition-all border ${activeTab === "events"
                ? "bg-green-600 text-white border-green-600 shadow-md"
                : "bg-white text-gray-700 border-gray-300 hover:bg-green-50"
                }`}
            >
              Events
            </button>
          </div>

          <AnimatePresence mode="wait">
            {loading ? (
              <div className="text-center text-gray-600">Loading articles...</div>
            ) : error ? (
              <div className="text-center text-red-600">{error}</div>
            ) : (
              <motion.ul
                key={activeTab}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }}
                className="space-y-4"
              >
                {filteredArticles.length > 0 ? (
                  filteredArticles.map((a, i) => (
                    <motion.li
                      key={a.id}
                      className="bg-white rounded-lg border hover:shadow-md transition overflow-hidden flex items-center gap-4 p-4"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      data-aos="fade-up"
                      data-aos-delay={(i + 1) * 40}
                    >
                      <img src={apiUrl + a.image} alt={a.title} className="w-28 h-20 object-cover rounded" />

                      <div className="flex-1">
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                          <span>{a.category_name}</span>
                          <span>{dayjs(a.created_at).format("DD MMM YYYY")}</span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          <Link to={`/article/${a.id}`} className="hover:text-green-600">
                            {a.title}
                          </Link>
                        </h3>
                        <div
                          className="text-gray-600"
                          dangerouslySetInnerHTML={{ __html: a.content.substring(0, 100) }}
                        />
                      </div>
                    </motion.li>
                  ))
                ) : (
                  <div className="text-center text-gray-500">
                    No {activeTab} available.
                  </div>
                )}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default ArticlesPage;